// Locale-aware formatting helpers. Numbers stay unchanged; only the display
// format and currency symbol are localised per the user's selection.

import { LANGUAGE_MAP, DEFAULT_LANGUAGE } from "@/i18n/config";

function meta(lang) {
  return LANGUAGE_MAP[lang] || LANGUAGE_MAP[DEFAULT_LANGUAGE];
}

export function formatCurrency(amount, lang) {
  const { locale, currency } = meta(lang);
  try {
    // Currencies like JPY have no fractional units — Intl handles that.
    return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

export function formatDateLong(isoDate, lang) {
  const { locale } = meta(lang);
  const d = new Date(`${isoDate}T12:00:00`);
  return new Intl.DateTimeFormat(locale, { weekday: "long", month: "long", day: "numeric" }).format(d);
}

export function formatDateMedium(isoDate, lang) {
  const { locale } = meta(lang);
  const d = new Date(`${isoDate}T12:00:00`);
  return new Intl.DateTimeFormat(locale, { weekday: "long", month: "short", day: "numeric" }).format(d);
}

export function formatWeekdayShort(date, lang) {
  const { locale } = meta(lang);
  return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date);
}

export function formatMonthShort(date, lang) {
  const { locale } = meta(lang);
  return new Intl.DateTimeFormat(locale, { month: "short" }).format(date);
}

export function formatMonthShortUpper(date, lang) {
  return formatMonthShort(date, lang).toUpperCase();
}

// Format a raw integer using the locale's numeral system so Arabic dates show
// Arabic-Indic digits (matching the currency), Bengali shows Bengali digits, etc.
export function formatNumber(value, lang) {
  const { locale } = meta(lang);
  try {
    return new Intl.NumberFormat(locale).format(value);
  } catch {
    return String(value);
  }
}

// Format a time (or slot range) using the locale's convention (some locales
// prefer 12h with AM/PM, others 24h). Accepts:
//   * 24h single time:  "13:00"
//   * 12h single time:  "1:00 PM"
//   * 12h range (slot): "1:00 PM - 3:00 PM"  (as returned by /api/slots)
// Returns input unchanged on failure.
function _parseSingleTime(s) {
  if (!s || typeof s !== "string") return null;
  // Match "H:MM" or "HH:MM" optionally followed by "AM"/"PM" (case-insensitive).
  const m = s.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3] ? m[3].toUpperCase() : null;
  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return { h, m: min };
}

function _formatOne(parsed, locale) {
  const d = new Date();
  d.setHours(parsed.h, parsed.m, 0, 0);
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(d);
}

export function formatTime(value, lang) {
  if (!value || typeof value !== "string") return value;
  const { locale } = meta(lang);
  try {
    // Range like "1:00 PM - 3:00 PM"
    if (value.includes(" - ")) {
      const [startStr, endStr] = value.split(" - ").map((s) => s.trim());
      const start = _parseSingleTime(startStr);
      const end = _parseSingleTime(endStr);
      if (start && end) return `${_formatOne(start, locale)} - ${_formatOne(end, locale)}`;
      return value;
    }
    const one = _parseSingleTime(value);
    if (!one) return value;
    return _formatOne(one, locale);
  } catch {
    return value;
  }
}
